/**
 * 打卡活动相关接口（与小程序 xf_crm_mini 保持一致，同一配置双端使用）
 */
import request from '@/utils/request'

const PREFIX = '/signstore'

/** 根据活动编码获取活动详情 */
export function getSignStoreActivityDetailByCode(params) {
  return request.get(PREFIX + '/activity/detailByCode', { params })
}

/** 逻拓科技 NFC H5 动态获取 URL Link（自动续期） */
export function getEnterpriseNfcRedirect(params) {
  return request.get(PREFIX + '/activity/nfcRedirect', { params })
}

/** 根据活动ID获取活动详情 */
export function getSignStoreActivityDetail(params) {
  return request.get(PREFIX + '/activity/detail', { params })
}

/**
 * 验证电子印章点位（统一打卡接口，与小程序完全一致）
 * @param {Object} data { ruleCode, touchPoints, activityUserId, operationType?, pointIndex? }
 * @returns {Promise<{ matched, errorMessage? }>}
 */
export function validateSeal(data) {
  return request.post(PREFIX + '/sealrule/compareSimilarity', data)
}

/** 获取或创建用户活动参与记录（小程序 / Token 场景） */
export function getOrCreateUserRecord(params) {
  return request.get(PREFIX + '/activityUser/getOrCreate', { params })
}

/** 获取或创建用户活动参与记录（H5 匿名用户，使用 h5Uuid 作为标识） */
export function getOrCreateUserRecordH5(params) {
  return request.get(PREFIX + '/activityUser/h5/getOrCreate', { params })
}

/** H5 匿名用户静默登录（按 h5Uuid 签发 token） */
export function h5AnonymousSilentLogin(body) {
  return request.post(PREFIX + '/activityUser/h5/silentLogin', body)
}

/** 查询用户已打卡的点位列表 */
export function getCheckedPoints(params) {
  return request.get(PREFIX + '/activityUser/checkedPoints', { params })
}

/** ACT004：查询用户各打卡点位周期状态（everChecked / cycleChecked） */
export function getPointCheckinStates(params) {
  return request.get(PREFIX + '/activityUser/pointCheckinStates', { params })
}

/** 根据商品ID数组批量获取商品详情 */
export function getProductListByIds(productIds) {
  return request.post('/store/product/listByIds', productIds)
}

/** ACT004 商品/积分商品热点兑换 */
export function redeemType10Product(data) {
  return request.post(PREFIX + '/activityUser/redeemProduct', data)
}

/** 查询当前活动可用积分（按用户+门店+活动隔离） */
export function getActivityAvailablePoints(params) {
  return request.get(PREFIX + '/activityUser/availablePoints', { params })
}

/** 查询当前用户在指定打卡点位的成功打卡排行（数据热点 CHECKIN_POINT_RANK） */
export function getCheckinPointRank(params) {
  return request.get(PREFIX + '/activityUser/checkinPointRank', { params })
}

/** ACT004：活动打卡次数排行榜（数据热点 ACTIVITY_CHECKIN_LEADERBOARD） */
export function getAct004CheckinLeaderboard(params) {
  return request.get(PREFIX + '/activityUser/act004CheckinLeaderboard', { params })
}

/** 查询已打卡点位及状态（pointIndex、isSignSuccess），用于 ACT002 勋章成功/失败回显 */
export function getCheckedPointsWithStatus(params) {
  return request.get(PREFIX + '/activityUser/checkedPointsWithStatus', { params })
}

/**
 * ACT004 多页打卡：印章比对 POST /signstore/sealrule/compareSimilarity/act004
 * @param {Object} data 须含 ruleCode、touchPoints、activityUserId（业务要求）、authStoreCode（必填，活动门店 store_code）等
 */
export function validateSealForAct004(data) {
  return request.post(PREFIX + '/sealrule/compareSimilarity/act004', data)
}

/** 查询指定规则的第一组标准点位 */
export function getFirstTemplatePoints(params) {
  return request.get(PREFIX + '/sealrule/firstTemplatePoints', { params })
}

/** 查询核销收货信息 */
export function getShippingInfo(params) {
  return request.get(PREFIX + '/activityUser/shippingInfo', { params })
}

/** 保存核销收货信息 */
export function saveShippingInfo(data) {
  return request.post(PREFIX + '/activityUser/shippingInfo', data)
}

/** ACT004：读取当前用户在该活动下的表单线索（sign_store_activity_form_lead） */
export function getAct004FormLeadLatest(params) {
  return request.get(PREFIX + '/activityUser/act004FormLeadLatest', { params })
}

/** ACT004：提交表单线索（sign_store_activity_form_lead） */
export function submitAct004FormLead(data) {
  return request.post(PREFIX + '/activityUser/act004FormLeadSubmit', data)
}

/** ACT004：读取当前用户表单资料（yx_user，兼容旧 H5） */
export function getAct004UserFormProfile() {
  return request.get(PREFIX + '/activityUser/act004UserFormProfile')
}

/** ACT004：提交用户信息表单（yx_user，兼容旧 H5） */
export function submitAct004UserForm(data) {
  return request.post(PREFIX + '/activityUser/act004UserFormSubmit', data)
}

/** ACT004 打卡限次详情（单点，含 quiz 预取） */
export function getAct004CheckinLimitStatusDetail(params) {
  return request.get(PREFIX + '/sealrule/checkinLimitStatusDetail/act004', { params })
}

/** ACT004 打卡限次详情批量（进入页预取问答/限次） */
export function getAct004CheckinLimitStatusDetailBatch(data) {
  return request.post(PREFIX + '/sealrule/checkinLimitStatusDetail/act004/batch', data)
}

/** ACT004 热点问答：开始会话 */
export function startAct004QuizSession(data) {
  return request.post(PREFIX + '/activityQuiz/session/start', data)
}

/** ACT004 热点问答：提交单题答案 */
export function submitAct004QuizAnswer(data) {
  return request.post(PREFIX + '/activityQuiz/session/answer', data)
}

/**
 * 摇一摇抽奖（入参 userId、lotteryConfigId；返回中奖 sign_store_lottery_prize 配置）
 * @param {Object} data { userId, lotteryConfigId, activityUserId?, hotspotIndex? }
 */
export function drawSignStoreLottery(data) {
  return request.post(PREFIX + '/lottery/h5/draw', data)
}

/**
 * 查询当前用户在本活动下的摇一摇抽奖记录（按热点回显奖品图）
 * @param {Object} params { activityUserId }
 */
export function listSignStoreLotteryDrawRecords(params) {
  return request.get(PREFIX + '/lottery/h5/drawRecords', { params })
}

/**
 * 摇一摇奖品印章核销
 * @param {Object} data { drawRecordId, userId, ruleCode, touchPoints, activityUserId?, authStoreCode? }
 */
export function verifySignStoreLotterySeal(data) {
  return request.post(PREFIX + '/lottery/h5/verifySeal', data)
}
